import { useState } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { X } from '@phosphor-icons/react';
import type { AnswerOptionCondition } from 'shared/types/due-diligence';

interface AILocationHintsEditorProps {
  condition: AnswerOptionCondition;
  onUpdate: (updates: Partial<AnswerOptionCondition>) => void;
}

export function AILocationHintsEditor({ condition, onUpdate }: AILocationHintsEditorProps) {
  const [keywordInput, setKeywordInput] = useState('');
  const hints = condition.aiHints || {};

  const handleHintUpdate = (field: 'nearPages' | 'nearTitle', value: string) => {
    onUpdate({
      aiHints: {
        ...hints,
        [field]: value || undefined,
      },
    });
  };

  const handleAddKeyword = () => {
    const trimmed = keywordInput.trim();
    if (!trimmed) return;

    const currentKeywords = hints.nearKeywords || [];
    if (!currentKeywords.includes(trimmed)) {
      onUpdate({
        aiHints: {
          ...hints,
          nearKeywords: [...currentKeywords, trimmed],
        },
      });
    }
    setKeywordInput('');
  };

  const handleRemoveKeyword = (keyword: string) => {
    const currentKeywords = hints.nearKeywords || [];
    onUpdate({
      aiHints: {
        ...hints,
        nearKeywords: currentKeywords.filter(k => k !== keyword),
      },
    });
  };

  const handleKeywordKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddKeyword();
    }
  };

  return (
    <div className="space-y-4 p-4 bg-muted/30 rounded-lg border border-border">
      <div className="font-medium text-sm text-foreground">Locatie hints (optioneel)</div>

      <div className="space-y-2">
        <Label htmlFor="nearPages" className="text-sm">
          Nabij pagina's
        </Label>
        <Input
          id="nearPages"
          placeholder="bijv. laatste pagina's, pagina 10"
          value={hints.nearPages || ''}
          onChange={(e) => handleHintUpdate('nearPages', e.target.value)}
          className="text-sm"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="nearTitle" className="text-sm">
          Nabij titel/sectie
        </Label>
        <Input
          id="nearTitle"
          placeholder="bijv. Conclusie, Handtekening"
          value={hints.nearTitle || ''}
          onChange={(e) => handleHintUpdate('nearTitle', e.target.value)}
          className="text-sm"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="keywords" className="text-sm">
          Zoekwoorden
        </Label>
        {hints.nearKeywords && hints.nearKeywords.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-2">
            {hints.nearKeywords.map((keyword) => (
              <Badge key={keyword} variant="secondary" className="gap-1 pl-2 pr-1">
                {keyword}
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-4 w-4 p-0 hover:bg-transparent"
                  onClick={() => handleRemoveKeyword(keyword)}
                >
                  <X size={12} weight="bold" />
                </Button>
              </Badge>
            ))}
          </div>
        )}
        <Input
          id="keywords"
          placeholder="Typ een zoekwoord en druk op Enter"
          value={keywordInput}
          onChange={(e) => setKeywordInput(e.target.value)}
          onKeyDown={handleKeywordKeyDown}
          className="text-sm"
        />
      </div>
    </div>
  );
}
